import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../utils/api';

const LOGO = '/img/noris-logo.svg';

// ── LOGIN ─────────────────────────────────────────────────────
export function LoginPage({ onSwitch }) {
  const { login }               = useAuth();
  const [form, setForm]         = useState({ phone: '', password: '' });
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await login(form.phone.trim(), form.password);
      if (!res.success) setError(res.message || 'Login failed');
    } catch {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  if (showForgot) return <ForgotPasswordPage onBack={() => setShowForgot(false)} />;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <img src={LOGO} alt="Noris" onError={e => e.target.style.display='none'} />
          <div><h1>NChat</h1><small>by Noris Solutions</small></div>
        </div>
        <h2 className="auth-title">Welcome back 👋</h2>
        <p className="auth-subtitle">Sign in to continue chatting</p>
        {error && <div className="form-error">⚠️ {error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <input className="form-input" type="tel" placeholder="+91 9876543210"
              value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" placeholder="Enter your password"
              value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
          </div>
          <div style={{ textAlign: 'right', marginBottom: 12 }}>
            <button type="button" onClick={() => setShowForgot(true)}
              style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 13 }}>
              Forgot password?
            </button>
          </div>
          <button className="btn btn-primary" type="submit" disabled={loading} style={{ marginTop: 4 }}>
            {loading ? '⏳ Signing in...' : '🔐 Sign In'}
          </button>
        </form>
        <div className="auth-switch">
          Don't have an account?{' '}
          <button onClick={onSwitch}>Create account</button>
        </div>
      </div>
    </div>
  );
}

// ── FORGOT PASSWORD ───────────────────────────────────────────
function ForgotPasswordPage({ onBack }) {
  const [step, setStep]         = useState(1); // 1=enter phone, 2=enter OTP+new pass
  const [phone, setPhone]       = useState('');
  const [otp, setOtp]           = useState('');
  const [newPass, setNewPass]   = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [msg, setMsg]           = useState('');
  const [devOtp, setDevOtp]     = useState(''); // remove in production

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setMsg(''); setLoading(true);
    try {
      const res = await authAPI.forgotPassword(phone.trim());
      if (res.data.success) {
        setDevOtp(res.data.data?.otp || ''); // dev only
        setStep(2);
        setMsg('A 6-digit reset code has been sent.');
      } else {
        setMsg('❌ ' + (res.data.message || 'Error'));
      }
    } catch { setMsg('❌ Network error.'); }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPass !== confirm) { setMsg('❌ Passwords do not match'); return; }
    if (newPass.length < 6) { setMsg('❌ Password must be at least 6 characters'); return; }
    setMsg(''); setLoading(true);
    try {
      const res = await authAPI.resetPassword({ phone: phone.trim(), otp: otp.trim(), new_password: newPass });
      if (res.data.success) {
        setMsg('✅ Password reset! Please sign in.');
        setTimeout(onBack, 2000);
      } else {
        setMsg('❌ ' + (res.data.message || 'Invalid OTP'));
      }
    } catch { setMsg('❌ Network error.'); }
    setLoading(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 14, marginBottom: 8 }}>
          ← Back to login
        </button>
        <h2 className="auth-title">Reset Password 🔑</h2>
        <p className="auth-subtitle">
          {step === 1 ? 'Enter your phone number to receive a reset code' : 'Enter the code and your new password'}
        </p>
        {msg && <div className={`form-error`} style={{ background: msg.startsWith('✅') ? '#E8F5E9' : undefined, color: msg.startsWith('✅') ? '#128C7E' : undefined }}>{msg}</div>}

        {step === 1 ? (
          <form onSubmit={handleRequestOtp}>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input className="form-input" type="tel" placeholder="+91 9876543210"
                value={phone} onChange={e => setPhone(e.target.value)} required />
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? '⏳ Sending...' : '📤 Send Reset Code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleReset}>
            {devOtp && (
              <div style={{ background: '#FFF3CD', border: '1px solid #FFC107', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12, color: '#856404' }}>
                🔧 Dev mode — OTP: <strong>{devOtp}</strong> (remove in production)
              </div>
            )}
            <div className="form-group">
              <label className="form-label">6-Digit Code</label>
              <input className="form-input" type="text" placeholder="123456" maxLength={6}
                value={otp} onChange={e => setOtp(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" placeholder="Min. 6 characters"
                value={newPass} onChange={e => setNewPass(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" placeholder="Repeat password"
                value={confirm} onChange={e => setConfirm(e.target.value)} required />
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? '⏳ Resetting...' : '🔐 Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── REGISTER ─────────────────────────────────────────────────
const BUSINESS_TYPES = [
  'Restaurant', 'Cafe', 'Grocery', 'Pharmacy', 'Hospital', 'Hotel',
  'Bank', 'School', 'Gym', 'Salon', 'Shop', 'Mechanic', 'Bakery',
  'Clinic', 'Petrol', 'Other',
];

export function RegisterPage({ onSwitch }) {
  const { register }          = useAuth();
  const [form, setForm]       = useState({
    name: '', phone: '', email: '', password: '', confirm: '',
    business_type: '', latitude: '', longitude: '', location_name: '',
  });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [locStatus, setLocStatus]   = useState(''); // 'ok' | 'denied' | 'error'

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        password: form.password,
        business_type: form.business_type || undefined,
        latitude:      form.latitude      || undefined,
        longitude:     form.longitude     || undefined,
        location_name: form.location_name || undefined,
      };
      const res = await register(payload);
      if (!res.success) setError(res.message || 'Registration failed');
    } catch {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) { setLocStatus('error'); return; }
    setLocLoading(true);
    setLocStatus('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        let locName = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const d = await r.json();
          if (d.display_name) locName = d.display_name.split(',').slice(0, 3).join(', ');
        } catch { /* ignore */ }
        setForm(prev => ({ ...prev, latitude: String(latitude), longitude: String(longitude), location_name: locName }));
        setLocLoading(false);
        setLocStatus('ok');
      },
      (err) => {
        setLocLoading(false);
        setLocStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'error');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const f = (key) => ({ value: form[key], onChange: e => setForm({ ...form, [key]: e.target.value }) });

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <img src={LOGO} alt="Noris" onError={e => e.target.style.display='none'} />
          <div><h1>NChat</h1><small>by Noris Solutions</small></div>
        </div>
        <h2 className="auth-title">Create your account</h2>
        <p className="auth-subtitle">Join NChat and start connecting</p>
        {error && <div className="form-error">⚠️ {error}</div>}
        <form onSubmit={handle}>
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input className="form-input" placeholder="Your full name" required {...f('name')} />
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number *</label>
            <input className="form-input" type="tel" placeholder="+91 9876543210" required {...f('phone')} />
          </div>
          <div className="form-group">
            <label className="form-label">Email (optional)</label>
            <input className="form-input" type="email" placeholder="you@example.com" {...f('email')} />
          </div>
          <div className="form-group">
            <label className="form-label">Password *</label>
            <input className="form-input" type="password" placeholder="Min. 6 characters" required {...f('password')} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm Password *</label>
            <input className="form-input" type="password" placeholder="Repeat password" required {...f('confirm')} />
          </div>

          {/* ── Business Type ── */}
          <div className="form-group">
            <label className="form-label">Business Type (optional)</label>
            <select className="form-input" value={form.business_type}
              onChange={e => setForm({ ...form, business_type: e.target.value })}
              style={{ cursor: 'pointer' }}>
              <option value="">— Select business type —</option>
              {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* ── Location ── */}
          <div className="form-group">
            <label className="form-label">Current Location (optional)</label>
            <button type="button" onClick={handleGetLocation} disabled={locLoading}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 10,
                border: '1.5px dashed var(--border-color)',
                background: locStatus === 'ok' ? '#E8F5E9' : 'var(--bg-primary)',
                color: locStatus === 'ok' ? '#2E7D32' : locStatus === 'denied' ? '#C62828' : 'var(--primary)',
                fontWeight: 600, fontSize: 14, cursor: locLoading ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all .2s',
              }}>
              {locLoading ? '⏳ Getting location…'
                : locStatus === 'ok'    ? `✅ ${form.location_name || 'Location saved'}`
                : locStatus === 'denied' ? '🚫 Permission denied — tap to retry'
                : locStatus === 'error'  ? '⚠️ Could not get location — tap to retry'
                : '📍 Use Current Location'}
            </button>
            {locStatus === 'denied' && (
              <p style={{ fontSize: 11, color: '#C62828', margin: '4px 0 0' }}>
                Allow location access in your browser settings, then tap again.
              </p>
            )}
          </div>

          <button className="btn btn-primary" type="submit" disabled={loading} style={{ marginTop: 8 }}>
            {loading ? '⏳ Creating account...' : '✅ Create Account'}
          </button>
        </form>
        <div className="auth-switch">
          Already have an account?{' '}
          <button onClick={onSwitch}>Sign in</button>
        </div>
      </div>
    </div>
  );
}
